export type CopilotMessageLike = {
  attachments?: unknown;
  content?: unknown;
  role?: unknown;
  toolCallId?: unknown;
  toolCalls?: unknown;
};

export type CopilotToolCallLike = Record<string, unknown>;

export type MessageAttachment = {
  downloadUrl?: string;
  id: string;
  mediaType?: "audio" | "document" | "image" | "unknown" | "video";
  mimeType?: string;
  name: string;
  size?: number;
  src?: string;
};

type InputContentPart = Record<string, unknown>;

type AttachmentSource = {
  mimeType?: unknown;
  type?: unknown;
  value?: unknown;
};

function isInputContentPart(value: unknown): value is InputContentPart {
  return Boolean(value && typeof value === "object" && "type" in value);
}

function getInputContentParts(content: unknown): InputContentPart[] {
  return Array.isArray(content) ? content.filter(isInputContentPart) : [];
}

function getMetadata(part: InputContentPart): Record<string, unknown> {
  return part.metadata && typeof part.metadata === "object"
    ? (part.metadata as Record<string, unknown>)
    : {};
}

function getAttachmentMimeType(
  part: InputContentPart,
  source: AttachmentSource | undefined,
): string {
  const metadata = getMetadata(part);

  return (
    (typeof source?.mimeType === "string" ? source.mimeType : undefined) ||
    (typeof part.mimeType === "string" ? part.mimeType : undefined) ||
    (typeof metadata.mimeType === "string" ? metadata.mimeType : undefined) ||
    "application/octet-stream"
  );
}

function getAttachmentName(part: InputContentPart, index: number): string {
  const metadata = getMetadata(part);

  return (
    (typeof metadata.filename === "string" ? metadata.filename : undefined) ||
    (typeof metadata.name === "string" ? metadata.name : undefined) ||
    (typeof part.filename === "string" ? part.filename : undefined) ||
    `attachment-${index + 1}`
  );
}

function getAttachmentSize(part: InputContentPart): number | undefined {
  const metadata = getMetadata(part);
  const size = metadata.size;

  return typeof size === "number" ? size : undefined;
}

function getSource(part: InputContentPart): AttachmentSource | undefined {
  if (part.source && typeof part.source === "object") return part.source as AttachmentSource;

  if (typeof part.url === "string") {
    return {mimeType: part.mimeType, type: "url", value: part.url};
  }

  if (typeof part.data === "string") {
    return {mimeType: part.mimeType, type: "data", value: part.data};
  }

  return undefined;
}

function getAttachmentUrl(
  source: AttachmentSource | undefined,
  mimeType: string,
): string | undefined {
  if (typeof source?.value !== "string") return undefined;

  if (source.type === "data" && !source.value.startsWith("data:")) {
    return `data:${mimeType};base64,${source.value}`;
  }

  return source.value;
}

function getMediaType(type: unknown, mimeType: string): MessageAttachment["mediaType"] {
  if (type === "image" || mimeType.startsWith("image/")) return "image";
  if (type === "audio" || mimeType.startsWith("audio/")) return "audio";
  if (type === "video" || mimeType.startsWith("video/")) return "video";
  if (type === "document" || type === "binary") return "document";

  return "unknown";
}

function isAttachmentPart(part: InputContentPart): boolean {
  return ["image", "audio", "video", "document", "binary"].includes(String(part.type));
}

export function getMessageRole(message: CopilotMessageLike): string {
  return typeof message.role === "string" ? message.role : "";
}

export function getMessageContentText(message: CopilotMessageLike): string {
  if (typeof message.content === "string") return message.content;

  return getInputContentParts(message.content)
    .map((part) => (part.type === "text" && typeof part.text === "string" ? part.text : ""))
    .filter((text) => text.length > 0)
    .join("\n");
}

export function getMessageAttachments(message: CopilotMessageLike): MessageAttachment[] {
  return getInputContentParts(message.content)
    .filter(isAttachmentPart)
    .map((part, index) => {
      const source = getSource(part);
      const mimeType = getAttachmentMimeType(part, source);
      const mediaType = getMediaType(part.type, mimeType);
      const url = getAttachmentUrl(source, mimeType);

      return {
        downloadUrl: url,
        id: `${String(part.type)}-${index}-${getAttachmentName(part, index)}`,
        mediaType,
        mimeType,
        name: getAttachmentName(part, index),
        size: getAttachmentSize(part),
        src: mediaType === "image" || mediaType === "video" ? url : undefined,
      };
    });
}

export function getMessageToolCalls(message: CopilotMessageLike): CopilotToolCallLike[] {
  return Array.isArray(message.toolCalls) ? (message.toolCalls as CopilotToolCallLike[]) : [];
}

export function isRenderableCopilotMessage(message: CopilotMessageLike): boolean {
  const role = getMessageRole(message);

  if (role === "tool" || role === "function" || role === "system") return false;

  const contentText = getMessageContentText(message);

  if (contentText.includes("[SYSTEM]")) return false;

  const attachments = getMessageAttachments(message);
  const toolCalls = getMessageToolCalls(message);
  const trimmedContent = contentText.trim();

  if (role === "user" && !trimmedContent && attachments.length === 0) return false;
  if (role === "assistant" && !trimmedContent && toolCalls.length === 0) return false;

  // Hide empty function/tool responses that we manually insert sometimes.
  if (role === "assistant" && trimmedContent.includes("[SYSTEM] Tool execution completed.")) {
    return false;
  }

  return true;
}

export function hasRenderableTextContent(message: CopilotMessageLike): boolean {
  const contentText = getMessageContentText(message);

  return Boolean(contentText && !contentText.includes("[SYSTEM]"));
}

export function getToolCallId(toolCall: CopilotToolCallLike): string | undefined {
  return typeof toolCall.id === "string" ? toolCall.id : undefined;
}

export function getToolCallName(toolCall: CopilotToolCallLike): string {
  const functionPayload = toolCall.function as Record<string, unknown> | undefined;

  return (
    (typeof functionPayload?.name === "string" ? functionPayload.name : undefined) ||
    (typeof toolCall.name === "string" ? toolCall.name : undefined) ||
    "Unknown tool"
  );
}

export function getToolCallArgumentsText(toolCall: CopilotToolCallLike): string {
  const functionPayload = toolCall.function as Record<string, unknown> | undefined;

  return typeof functionPayload?.arguments === "string" ? functionPayload.arguments : "{}";
}

export function parseToolCallArgs(toolCall: CopilotToolCallLike): Record<string, unknown> {
  try {
    const parsed = JSON.parse(getToolCallArgumentsText(toolCall));

    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function findToolMessageForCall(
  messages: CopilotMessageLike[],
  toolCallId: string | undefined,
): CopilotMessageLike | undefined {
  if (!toolCallId) return undefined;

  return messages.find(
    (message) => getMessageRole(message) === "tool" && message.toolCallId === toolCallId,
  );
}

export function getToolCallDisplayStatus(
  toolCall: CopilotToolCallLike,
  toolMessage: CopilotMessageLike | undefined,
): string {
  if (toolMessage) return "complete";

  return (
    (typeof toolCall.status === "string" ? toolCall.status : undefined) ||
    (typeof toolCall.state === "string" ? toolCall.state : undefined) ||
    "inProgress"
  );
}
